import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, throwError } from 'rxjs';
import { environment } from '../../environment/environment';
import {
  TrainingCategoryBackendListModel,
  TrainingCategoryBackendModel,
  TrainingCategoryCreateUpdateModel,
  TrainingCategoryModel,
} from '../models/training-category.model';

@Injectable({
  providedIn: 'root',
})
export class TrainingCategoryService {
  constructor(private http: HttpClient) {}

  getTrainingCategoryById(id: number): Observable<TrainingCategoryModel> {
    const formData = new FormData();
    formData.append('mode', 'getCategory');
    formData.append('id', id.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: TrainingCategoryBackendModel;
      }>(environment.authority_short + 'category.php', formData)
      .pipe(
        map(response => this.parseBackendListModel(response.data)),
        catchError(() => {
          return throwError(() => new Error('Category fetch failed'));
        })
      );
  }

  public getAllTrainingCategories(
    includeDisabled: boolean = false
  ): Observable<TrainingCategoryModel[]> {
    const formData = new FormData();
    formData.append('mode', 'getAllCategories');
    formData.append('include_disabled', includeDisabled ? '1' : '0');

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: TrainingCategoryBackendListModel[];
      }>(environment.authority_short + 'category.php', formData)
      .pipe(
        map(response =>
          response.data?.map(type => this.parseBackendListModel(type))
        ),
        catchError(() => {
          return throwError(() => new Error('Categories fetch failed'));
        })
      );
  }

  public createTrainingCategory(
    trainingCategory: TrainingCategoryCreateUpdateModel
  ): Observable<void> {
    const formData = new FormData();
    formData.append('mode', 'createCategory');
    formData.append('name', trainingCategory.name);
    formData.append('color50', trainingCategory.color50);
    formData.append('color0', trainingCategory.color0);
    formData.append('color70', trainingCategory.color70);
    formData.append('color80', trainingCategory.color80);
    formData.append('color90', trainingCategory.color90);
    formData.append('description_bold', trainingCategory.descriptionBold);
    formData.append('description', trainingCategory.description);
    formData.append('title_picture', trainingCategory.title_picture);
    formData.append(
      'sponsor_card_ids',
      JSON.stringify(trainingCategory.sponsorCardIds)
    );

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'category.php', formData)
      .pipe(
        map(() => undefined),
        catchError(() => {
          return throwError(() => new Error('Category creation failed'));
        })
      );
  }

  public updateTrainingCategory(
    trainingCategory: TrainingCategoryCreateUpdateModel
  ): Observable<void> {
    const formData = new FormData();
    formData.append('mode', 'updateCategory');
    formData.append('id', trainingCategory.id.toString());
    formData.append('name', trainingCategory.name);
    formData.append('color50', trainingCategory.color50);
    formData.append('color0', trainingCategory.color0);
    formData.append('color70', trainingCategory.color70);
    formData.append('color80', trainingCategory.color80);
    formData.append('color90', trainingCategory.color90);
    formData.append('description_bold', trainingCategory.descriptionBold);
    formData.append('description', trainingCategory.description);
    formData.append('title_picture', trainingCategory.title_picture);
    formData.append(
      'sponsor_card_ids',
      JSON.stringify(trainingCategory.sponsorCardIds)
    );

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'category.php', formData)
      .pipe(
        map(() => undefined),
        catchError(() => {
          return throwError(() => new Error('Category update failed'));
        })
      );
  }

  public deleteCourseType(id: number): Observable<void> {
    const formData = new FormData();
    formData.append('mode', 'deleteCategory');
    formData.append('id', id.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'category.php', formData)
      .pipe(
        map(() => undefined),
        catchError(() => {
          return throwError(() => new Error('Category delete failed'));
        })
      );
  }

  public toggleSeminarCategory(id: number): Observable<void> {
    const formData = new FormData();
    formData.append('mode', 'toggleCategory');
    formData.append('id', id.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'category.php', formData)
      .pipe(
        map(() => undefined),
        catchError(() => {
          return throwError(() => new Error('Toggle Category failed'));
        })
      );
  }

  public parseBackendModel(
    backendModel: TrainingCategoryBackendModel
  ): TrainingCategoryModel {
    return {
      id: backendModel.id,
      name: backendModel.name,
      color50: backendModel.color50,
      color0: backendModel.color0,
      color70: backendModel.color70,
      color80: backendModel.color80,
      color90: backendModel.color90,
      descriptionBold: backendModel.description_bold,
      description: backendModel.description,
      titlePicture: backendModel.title_picture,
      courseCount: backendModel.course_count,
      isDisabled: backendModel.is_disabled,
    };
  }

  public parseBackendListModel(
    backendModel: TrainingCategoryBackendModel
  ): TrainingCategoryModel {
    return {
      id: backendModel.id,
      name: backendModel.name,
      color50: backendModel.color50,
      color0: backendModel.color0,
      color70: backendModel.color70,
      color80: backendModel.color80,
      color90: backendModel.color90,
      descriptionBold: backendModel.description_bold,
      description: backendModel.description,
      titlePicture: backendModel.title_picture,
      courseCount: backendModel.course_count,
      isDisabled: backendModel.is_disabled,
      sponsorCardIds: backendModel.sponsor_card_ids,
    };
  }
}
